import { useCallback, useEffect, useState } from 'react';
import Cookie from 'js-cookie';
import { EV_COOKIE_CONSENT_UPDATED, ICookie } from '.';
import Events from '../../utils/events';

const defaultSettings = [
  { name: 'necessaryCookies', displayName: 'Nödvändiga cookies', optional: false, active: true },
  { name: 'functionalCookies', displayName: 'Funktionscookies', optional: true, active: false },
  { name: 'analyticsCookies', displayName: 'Webbplatsanpassning', optional: true, active: false },
  { name: 'marketingCookies', displayName: 'Marknadsföringcookies', optional: true, active: false },
];

const fromCookie = () =>
  JSON.parse(Cookie.get(COOKIE_NAME) || 'null') as Record<string, boolean> | null;

export const getCookieSettings = () => fromCookie();

const toCookie = (cookies: ICookie[]) =>
  JSON.stringify(cookies.reduce((acc, c) => ({ ...acc, [c.name]: c.active }), {}));

const initializeSettings = (consentCookie: Record<string, boolean> | null): ICookie[] => {
  if (!consentCookie) {
    return [];
  }

  return Object.keys(consentCookie).map((key) => {
    const _default = defaultSettings.find((s) => s.name === key);

    return _default ? { ..._default, active: consentCookie[key] } : null;
  }) as ICookie[];
};

const COOKIE_NAME = 'mioCookieConsent';

export default () => {
  const consentCookie = fromCookie();
  const [settings, setSettings] = useState<ICookie[]>(() => initializeSettings(consentCookie));

  const [draftSettings, setDraftSettings] = useState<ICookie[]>(
    settings.length ? settings : defaultSettings,
  );

  const allowAll = useCallback(() => {
    const allApproved = defaultSettings.map((d) => ({ ...d, active: true }));
    setSettings(allApproved);
    setDraftSettings(allApproved);
  }, []);

  const allowOnlyNecessary = useCallback(() => {
    setSettings(defaultSettings);
  }, []);

  useEffect(() => {
    if (!settings.length) {
      return;
    }

    const isUpdated =
      !consentCookie ||
      !Object.keys(consentCookie).every(
        (key) => settings.find((s) => s.name === key)?.active === !!consentCookie[key],
      );

    if (isUpdated) {
      Cookie.set(COOKIE_NAME, toCookie(settings), { expires: 90 });

      const nodes = document.head.querySelectorAll<HTMLScriptElement>('[data-category]');
      Array.from(nodes).forEach((n) => {
        const category = n.dataset.category;
        const categoryConsent = !!category && settings.find(({ name }) => name === category);
        const active = (categoryConsent as ICookie)?.active;
        const activated = n.type === 'text/javascript';

        if (active && !activated) {
          n.type = 'text/javascript';
          document.head.replaceChild(n, n);
        }

        if (!active && activated) {
          n.type = 'text/plain';
          document.head.replaceChild(n, n);
        }
      });

      Events.emit(EV_COOKIE_CONSENT_UPDATED, settings);
    }
  }, [consentCookie, settings]);

  return {
    allowAll,
    consenting: !!settings.length,
    draftSettings,
    setDraftSettings,
    setSettings,
    settings,
    allowOnlyNecessary,
  };
};
